/**
 * Channel definitions for CloudSam Survey Designer
 *
 * Defines supported delivery channels, their capabilities,
 * and the validation rules for survey content compatibility.
 *
 * Email is deliberately not here. It is how a recipient is invited to take a
 * survey, not a medium the survey is conducted in — an emailed invitation
 * lands the respondent in the browser, which is already a channel.
 *
 * Reference: survey-channels/design docs/Survey_Designer_Changes.md
 */
import React from 'react';
import { Globe, MessageSquare, Smartphone, BookOpen, MessageSquareDashed, MessagesSquare, PanelTop, Hash } from 'lucide-react';

// ---------------------------------------------------------------------------
// Channel type identifiers (V1)
// ---------------------------------------------------------------------------

export const CHANNEL_TYPES = {
  BROWSER: 'browser',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
  RCS: 'rcs',
  MESSENGER: 'messenger',
  GOOGLECHAT: 'googlechat',
  MSTEAMS: 'msteams',
  SLACK: 'slack',
};

// ---------------------------------------------------------------------------
// Channel metadata – labels, icons and short descriptions for the UI
// ---------------------------------------------------------------------------

export const CHANNEL_META = {
  [CHANNEL_TYPES.BROWSER]: {
    label: 'Browser',
    icon: <Globe size={14} />,
    description: 'Survey runs in the browser; invitations are sent by email.',
  },
  [CHANNEL_TYPES.SMS]: {
    label: 'SMS',
    icon: <MessageSquare size={14} />,
    description: 'Text only. No images, no interactive buttons. Numbered choice lists.',
  },
  [CHANNEL_TYPES.WHATSAPP]: {
    label: 'WhatsApp',
    icon: <Smartphone size={14} />,
    description: 'Text, images, buttons (up to 3), list messages (up to 10 items)',
  },
  [CHANNEL_TYPES.RCS]: {
    label: 'RCS',
    icon: <MessageSquareDashed size={14} />,
    description: 'Certified rich cards and media where the recipient route supports them.',
  },
  [CHANNEL_TYPES.MESSENGER]: {
    label: 'Messenger',
    icon: <BookOpen size={14} />,
    description: 'Text, images, buttons, quick replies',
  },
  [CHANNEL_TYPES.GOOGLECHAT]: {
    label: 'Google Chat',
    icon: <MessagesSquare size={14} />,
    description: 'Card v2 buttons, text input, images. Workspace/B2B environments.',
  },
  [CHANNEL_TYPES.MSTEAMS]: {
    label: 'Microsoft Teams',
    icon: <PanelTop size={14} />,
    description: 'Text survey delivery in Teams. Choices use numbered text responses.',
  },
  [CHANNEL_TYPES.SLACK]: {
    label: 'Slack',
    icon: <Hash size={14} />,
    description: 'Text survey delivery in Slack. Choices use numbered text responses.',
  },
};

// ---------------------------------------------------------------------------
// Capability identifiers
// ---------------------------------------------------------------------------

export const CAPABILITIES = {
  TEXT_DISPLAY: 'text_display',
  IMAGE_DISPLAY: 'image_display',
  VIDEO_DISPLAY: 'video_display',
  AUDIO_DISPLAY: 'audio_display',
  INTERACTIVE_BUTTONS: 'interactive_buttons',
  CHOICE_LIST_LARGE: 'choice_list_large',     // >3 items
  CHOICE_LIST_EXTENDED: 'choice_list_extended',   // >10 items
  FILE_UPLOAD: 'file_upload',
  SLIDER_INPUT: 'slider_input',
  DRAG_DROP: 'drag_drop',
  VOICE_INPUT: 'voice_input',
};

// ---------------------------------------------------------------------------
// Capability matrix
//
// true      = full support
// 'partial' = works with degraded experience
// false     = not supported
// ---------------------------------------------------------------------------

export const CAPABILITY_MATRIX = {
  [CHANNEL_TYPES.BROWSER]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: true,
    [CAPABILITIES.VIDEO_DISPLAY]: true,
    [CAPABILITIES.AUDIO_DISPLAY]: true,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: true,
    [CAPABILITIES.CHOICE_LIST_LARGE]: true,
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: true,
    [CAPABILITIES.FILE_UPLOAD]: true,
    [CAPABILITIES.SLIDER_INPUT]: true,
    [CAPABILITIES.DRAG_DROP]: true,
    [CAPABILITIES.VOICE_INPUT]: true,
  },
  [CHANNEL_TYPES.SMS]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: false,         // link only
    [CAPABILITIES.VIDEO_DISPLAY]: false,
    [CAPABILITIES.AUDIO_DISPLAY]: false,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: false,
    [CAPABILITIES.CHOICE_LIST_LARGE]: 'partial',     // numbered text list
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: 'partial',     // numbered text list
    [CAPABILITIES.FILE_UPLOAD]: false,
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: false,
  },
  [CHANNEL_TYPES.WHATSAPP]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: true,
    [CAPABILITIES.VIDEO_DISPLAY]: 'partial',
    [CAPABILITIES.AUDIO_DISPLAY]: true,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: true,          // up to 3
    [CAPABILITIES.CHOICE_LIST_LARGE]: true,          // list message
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: true,          // max 10 items
    [CAPABILITIES.FILE_UPLOAD]: true,          // attachments
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: true,          // voice messages
  },
  [CHANNEL_TYPES.RCS]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,              // delivered via TTS
    [CAPABILITIES.IMAGE_DISPLAY]: true,
    [CAPABILITIES.VIDEO_DISPLAY]: 'partial',
    [CAPABILITIES.AUDIO_DISPLAY]: 'partial',
    [CAPABILITIES.INTERACTIVE_BUTTONS]: true,
    [CAPABILITIES.CHOICE_LIST_LARGE]: true,
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: 'partial',
    [CAPABILITIES.FILE_UPLOAD]: false,
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: false,
  },
  [CHANNEL_TYPES.MESSENGER]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: true,
    [CAPABILITIES.VIDEO_DISPLAY]: 'partial',
    [CAPABILITIES.AUDIO_DISPLAY]: true,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: true,
    [CAPABILITIES.CHOICE_LIST_LARGE]: true,          // quick replies
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: true,
    [CAPABILITIES.FILE_UPLOAD]: true,          // attachments
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: true,          // voice messages
  },
  [CHANNEL_TYPES.GOOGLECHAT]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: true,
    [CAPABILITIES.VIDEO_DISPLAY]: false,
    [CAPABILITIES.AUDIO_DISPLAY]: false,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: true,        // Card v2 buttons
    [CAPABILITIES.CHOICE_LIST_LARGE]: true,          // radio buttons in cards
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: true,       // scrollable card sections
    [CAPABILITIES.FILE_UPLOAD]: false,
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: false,
  },
  [CHANNEL_TYPES.MSTEAMS]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: false,
    [CAPABILITIES.VIDEO_DISPLAY]: false,
    [CAPABILITIES.AUDIO_DISPLAY]: false,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: false,
    [CAPABILITIES.CHOICE_LIST_LARGE]: 'partial',
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: 'partial',
    [CAPABILITIES.FILE_UPLOAD]: false,
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: false,
  },
  [CHANNEL_TYPES.SLACK]: {
    [CAPABILITIES.TEXT_DISPLAY]: true,
    [CAPABILITIES.IMAGE_DISPLAY]: false,
    [CAPABILITIES.VIDEO_DISPLAY]: false,
    [CAPABILITIES.AUDIO_DISPLAY]: false,
    [CAPABILITIES.INTERACTIVE_BUTTONS]: false,
    [CAPABILITIES.CHOICE_LIST_LARGE]: 'partial',
    [CAPABILITIES.CHOICE_LIST_EXTENDED]: 'partial',
    [CAPABILITIES.FILE_UPLOAD]: false,
    [CAPABILITIES.SLIDER_INPUT]: false,
    [CAPABILITIES.DRAG_DROP]: false,
    [CAPABILITIES.VOICE_INPUT]: false,
  },
};

// ---------------------------------------------------------------------------
// Channel-specific limits
// ---------------------------------------------------------------------------

export const CHANNEL_LIMITS = {
  [CHANNEL_TYPES.WHATSAPP]: {
    maxButtons: 3,
    maxListItems: 10,
  },
};

// ---------------------------------------------------------------------------
// Node-to-capability mapping
//
// Each entry describes a node property that requires a specific capability.
// `condition` is a tag the validator uses to match node content.
// `degradation` explains what happens when the channel lacks the capability.
// ---------------------------------------------------------------------------

export const NODE_CAPABILITY_REQUIREMENTS = [
  {
    condition: 'has_image',
    capability: CAPABILITIES.IMAGE_DISPLAY,
    degradation: 'Will send as URL link instead',
  },
  {
    condition: 'has_video',
    capability: CAPABILITIES.VIDEO_DISPLAY,
    degradation: 'Limited or no video support on this channel',
  },
  {
    condition: 'has_slider',
    capability: CAPABILITIES.SLIDER_INPUT,
    degradation: 'Slider input not supported; question may be skipped or adapted',
  },
  {
    condition: 'has_ranking',
    capability: CAPABILITIES.DRAG_DROP,
    degradation: 'Drag-and-drop ranking not supported on this channel',
  },
  {
    condition: 'has_file_upload',
    capability: CAPABILITIES.FILE_UPLOAD,
    degradation: 'Cannot receive files via this channel',
  },
  {
    condition: 'choices_exceed_3',
    capability: CAPABILITIES.INTERACTIVE_BUTTONS,
    channelSpecific: CHANNEL_TYPES.WHATSAPP,
    degradation: 'WhatsApp max 3 buttons; will use list message instead',
  },
  {
    condition: 'choices_exceed_10',
    capability: CAPABILITIES.CHOICE_LIST_EXTENDED,
    channelSpecific: CHANNEL_TYPES.WHATSAPP,
    degradation: 'WhatsApp list messages support max 10 items',
  },
];

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Default target_channels for new surveys */
export const DEFAULT_TARGET_CHANNELS = ['browser'];

/** Display order for the channel checkbox list in the UI */
export const CHANNEL_DISPLAY_ORDER = [
  CHANNEL_TYPES.BROWSER,
  CHANNEL_TYPES.GOOGLECHAT,
  CHANNEL_TYPES.MSTEAMS,
  CHANNEL_TYPES.SLACK,
  CHANNEL_TYPES.SMS,
  CHANNEL_TYPES.RCS,
  CHANNEL_TYPES.WHATSAPP,
  CHANNEL_TYPES.MESSENGER,
];
